import type { ReactNode } from 'react';
import type { FileRejection } from 'react-dropzone';
import cn from 'classnames';
import { Button, Checkbox, Spinner, Tooltip } from '@heroui/react';
import { motion, Reorder, useDragControls } from 'motion/react';
import {
  StepScormServerStatus,
  StepScormUploadStatusCard,
  isScormProcessing,
  isScormUploadResumable,
  useStepScormUpload,
} from '@/features/StepScormUploads';
import {
  LessonVideoPreview,
  mapLessonVideo,
  StepVideoServerStatus,
  StepVideoUploadStatusCard,
  isVideoUploadInterrupted,
  isVideoUploadResumable,
  useStepVideoUpload,
} from '@/features/StepVideoUploads';
import type { AdminStepScorm, ContentStep, ContentStepType } from '@/entities/Courses';
import type { IStreamVideo } from '@/shared/types/common';
import { CustomInput, CustomSelect, CustomTextarea } from '@/shared/ui/Fields';
import { Dropzone } from '@/shared/ui/Dropzone';
import { Svg } from '@/shared/ui/Svg';
import UploadVideoIcon from '@/shared/assets/icons/files/upload-video.svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import CloseIcon from '@/shared/assets/icons/close-sm.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_HINT } from '@/shared/lib/constants/fileSizes';
import {
  SESSION_ATTACHMENT_ACCEPT,
  SESSION_ATTACHMENT_TYPES_HINT,
} from '../../../config/sessionAttachmentAccept';
import { SCORM_STATUS_BADGE } from '../../../config/scormStatusBadge';
import {
  SCORM_MAX_SIZE,
  STEP_CONTENT_MAX_LENGTH,
  STEP_TYPE_OPTIONS,
  STEP_VIDEO_MAX_SIZE,
  getPassingScoreOptions,
  type SessionStepDraft,
  type StepLiveActions,
} from './types';
import styles from './SessionStepsField.module.scss';

export const tooltipClassNames = {
  content: 'max-w-[min(320px,92vw)] px-3 py-2 bg-[#211D55] text-white text-sm rounded-none',
};

export type StepMediaKind = 'video' | 'scorm';

interface StepCardProps {
  step: SessionStepDraft;
  index: number;
  // Latest server state of the step: video and SCORM status, saved files
  serverStep?: ContentStep;
  canDelete: boolean;
  live?: StepLiveActions;
  onUpdate: (patch: Partial<SessionStepDraft>) => void;
  onRequestDelete: () => void;
  onRequestRemoveMedia: (kind: StepMediaKind) => void;
  onRequestReplaceScorm: (file: File) => void;
  onRejected: (rejections: FileRejection[]) => void;
  onPreviewScorm?: (packageId: number) => void;
  isScormPreviewLoading?: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onMove: (direction: -1 | 1) => void;
}

const typeLabel = (type: ContentStepType | '') =>
  STEP_TYPE_OPTIONS.find((option) => option.key === type)?.label ?? 'Content type';

const getVideoDropzoneLabel = (video: IStreamVideo | null) => {
  if (!video) return 'Upload Video';
  // The same file continues the interrupted upload, a different one replaces the video
  return isVideoUploadResumable(video) ? 'Continue or replace the video' : 'Upload another video';
};

const getScormDropzoneLabel = (scorm: AdminStepScorm | null | undefined) => {
  if (!scorm) return 'Upload a SCORM package';
  if (scorm.processingStatus !== 'uploading') return 'Replace the SCORM package';
  // The same file continues the interrupted upload, a different one replaces the package
  return isScormUploadResumable(scorm)
    ? 'Continue or replace the SCORM package'
    : 'Upload the SCORM package again';
};

const DropzoneCopy = ({ title, hints }: { title: string; hints: string[] }) => (
  <div className={styles.dropzoneCopy}>
    <Svg Icon={UploadVideoIcon} width={40} height={40} className="text-red" />
    <div className="flex flex-col gap-y-1">
      <p className="text-black font-bold text-sm">{title}</p>
      {hints.map((hint) => (
        <p key={hint} className="text-grey-description text-xs">
          {hint}
        </p>
      ))}
    </div>
  </div>
);

const RemoveButton = ({
  label,
  onPress,
  isDisabled = false,
}: {
  label: string;
  onPress: () => void;
  isDisabled?: boolean;
}) => (
  <button
    type="button"
    className="flex w-fit cursor-pointer items-center gap-1.5 text-sm text-red-tertiary hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
    disabled={isDisabled}
    onClick={onPress}
  >
    <Svg Icon={TrashIcon} width={16} height={16} />
    {label}
  </button>
);

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span className="text-sm font-medium text-black-headline">{children}</span>
);

export const StepCard = ({
  step,
  index,
  serverStep,
  canDelete,
  live,
  onUpdate,
  onRequestDelete,
  onRequestRemoveMedia,
  onRequestReplaceScorm,
  onRejected,
  onPreviewScorm,
  isScormPreviewLoading = false,
  onDragStart,
  onDragEnd,
  onMove,
}: StepCardProps) => {
  const dragControls = useDragControls();
  // Uploads of this step's video and SCORM package in the uploads managers: running, or failed
  // and waiting for a retry
  const videoUpload = useStepVideoUpload(live ? step.serverId : undefined);
  const scormUpload = useStepScormUpload(live ? step.serverId : undefined);

  const saved = serverStep ?? step.existing;
  const savedVideo =
    step.type === 'video' && !step.removeVideo ? mapLessonVideo(saved?.video) : null;
  const savedScorm = step.type === 'scorm' && !step.removeScorm ? saved?.scorm : null;
  const scormBadge = savedScorm ? SCORM_STATUS_BADGE[savedScorm.processingStatus] : null;
  const isTypeLocked =
    (!!saved?.video && !step.removeVideo) ||
    (!!saved?.scorm && !step.removeScorm) ||
    !!videoUpload ||
    !!scormUpload;
  const dataBaseFiles = (saved?.attachments ?? []).filter(
    (file) => !step.removedAttachmentKeys.includes(file.key),
  );
  const isVideoUploading = !!videoUpload && videoUpload.status !== 'error';
  const isScormUploading = !!scormUpload && scormUpload.status !== 'error';
  const isSaving = !!live?.isStepSaving(step.clientId);
  // A rejected video or an upload that never finished is replaced straight from the dropzone
  const canReplaceSavedVideo =
    !!savedVideo && (savedVideo.status === 'error' || isVideoUploadInterrupted(savedVideo));
  const isSavedScormProcessing = isScormProcessing(savedScorm?.processingStatus);
  // The score settings went with the file: the backend takes changes once the upload is confirmed
  const isScormSettingsLocked =
    (!!scormUpload && scormUpload.status !== 'uploaded') ||
    savedScorm?.processingStatus === 'uploading';

  const saveFields = () => {
    live?.saveFields(step);
  };

  const changeType = (type: ContentStepType | '') => {
    onUpdate({
      type,
      videoFile: type === 'video' ? step.videoFile : null,
      scormFile: type === 'scorm' ? step.scormFile : null,
    });
    if (live && type) live.saveFields(step, { type });
  };

  const changeScormSettings = (scormGraded: boolean, scormPassingScore: number) => {
    onUpdate({ scormGraded, scormPassingScore });
    // Without a package the settings are sent together with the uploaded file
    if (live && savedScorm) live.saveScormSettings(step, scormGraded, scormPassingScore);
  };

  const selectScormFile = (file: File | null) => {
    if (!live) {
      onUpdate({ scormFile: file, scormUploaded: false });
      return;
    }
    if (!file) return;
    // Replacing a package students may already use needs a confirmation; an unfinished upload
    // is continued with the same file or replaced right away
    if (savedScorm && savedScorm.processingStatus !== 'uploading') onRequestReplaceScorm(file);
    else live.startScormUpload(step, index + 1, file, savedScorm);
  };

  const changeAttachments = (files: File[]) => {
    onUpdate({ attachments: files });
    if (live) live.uploadAttachments(step, files.filter((file) => !step.attachments.includes(file)));
  };

  const removeSavedAttachment = (key: string) => {
    if (live) live.removeAttachment(step, key);
    else onUpdate({ removedAttachmentKeys: [...step.removedAttachmentKeys, key] });
  };

  return (
    <Reorder.Item
      as="div"
      value={step.clientId}
      className={styles.row}
      // Only the grip starts a drag, so text in the fields can still be selected
      dragListener={false}
      dragControls={dragControls}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      // Only the position is animated: animating the size would stretch the card on expand
      layout="position"
      whileDrag="dragging"
      variants={{ dragging: { scale: 1.02, zIndex: 10 } }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
    >
      <div
        role="button"
        tabIndex={0}
        className={styles.grip}
        aria-label={`Reorder step ${index + 1}. Use the arrow keys to move it`}
        onPointerDown={(event) => {
          event.preventDefault();
          dragControls.start(event);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            onMove(event.key === 'ArrowUp' ? -1 : 1);
          }
        }}
      >
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <motion.div
        className={styles.card}
        variants={{ dragging: { boxShadow: '0 12px 32px rgba(33, 29, 85, 0.18)' } }}
      >
        <div className={styles.headerTop}>
          <div className="flex items-center gap-2">
            <span className={styles.stepIndex}>Step {index + 1}</span>
            {isSaving && (
              <span className="flex items-center gap-1.5 text-xs text-grey-secondary">
                <Spinner size="sm" color="default" />
                Saving...
              </span>
            )}
          </div>
          <Tooltip
            content="A session must have at least one step"
            placement="top-end"
            isDisabled={canDelete}
            classNames={tooltipClassNames}
          >
            <span>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label={`Delete step ${index + 1}`}
                disabled={!canDelete || isVideoUploading || isScormUploading}
                onClick={onRequestDelete}
              >
                <Svg Icon={CloseIcon} width={16} height={16} />
              </button>
            </span>
          </Tooltip>
        </div>
        <button
          type="button"
          className={cn(styles.summary, { [styles.summaryOpen]: step.expanded })}
          aria-expanded={step.expanded}
          onClick={() => onUpdate({ expanded: !step.expanded })}
        >
          <span className={styles.summaryType}>{typeLabel(step.type)}</span>
          <span className={styles.summaryTitle}>{step.title.trim()}</span>
          <Svg
            Icon={ChevronDownIcon}
            width={20}
            height={20}
            className={cn(styles.chevron, { [styles.chevronOpen]: step.expanded })}
          />
        </button>
        {step.expanded && (
          <div className={styles.body}>
            <CustomInput
              label="Title"
              labelPlacement="outside"
              placeholder={`Step ${index + 1}`}
              maxLength={255}
              value={step.title}
              onValueChange={(title) => onUpdate({ title })}
              onBlur={saveFields}
            />
            <div className="flex flex-col gap-1">
              <CustomSelect
                style="black"
                label="Content Type"
                labelPlacement="outside"
                placeholder="Select Content"
                items={STEP_TYPE_OPTIONS}
                isDisabled={isTypeLocked}
                disallowEmptySelection={!!live}
                selectedKeys={step.type ? new Set([step.type]) : new Set()}
                onSelectionChange={(keys) => {
                  const value = Array.from(keys as Set<string>)[0] as ContentStepType | undefined;
                  changeType(value ?? '');
                }}
              />
              {isTypeLocked && (
                <p className="text-xs text-grey-secondary">
                  The type can&apos;t be changed while the step has a video or a SCORM package
                </p>
              )}
            </div>
            <CustomTextarea
              label="Description"
              labelPlacement="outside"
              placeholder="Enter description..."
              maxLength={255}
              value={step.description}
              onValueChange={(description) => onUpdate({ description })}
              onBlur={saveFields}
            />
            {step.type && (
              <>
                <div className={styles.divider} />
                <CustomTextarea
                  label="Content"
                  labelPlacement="outside"
                  placeholder="Enter text here..."
                  maxLength={STEP_CONTENT_MAX_LENGTH}
                  value={step.content}
                  onValueChange={(content) => onUpdate({ content })}
                  onBlur={saveFields}
                />
              </>
            )}
            {step.type === 'video' && (
              <div className="flex flex-col gap-2">
                {videoUpload && live ? (
                  <StepVideoUploadStatusCard
                    upload={videoUpload}
                    onCancel={() => live.cancelVideoUpload(step)}
                    onRetry={() => live.retryVideoUpload(step)}
                  />
                ) : savedVideo?.status === 'ready' && !step.videoFile ? (
                  <>
                    <LessonVideoPreview video={savedVideo} />
                    <RemoveButton
                      label="Remove video"
                      onPress={() => onRequestRemoveMedia('video')}
                    />
                  </>
                ) : (
                  <>
                    {savedVideo && !step.videoFile && (
                      <>
                        <StepVideoServerStatus video={savedVideo} />
                        <RemoveButton
                          label="Remove video"
                          onPress={() => onRequestRemoveMedia('video')}
                        />
                      </>
                    )}
                    {(!savedVideo || !!step.videoFile || canReplaceSavedVideo) && (
                      <>
                        <FieldLabel>{getVideoDropzoneLabel(savedVideo)}</FieldLabel>
                        <Dropzone
                          setFile={(videoFile) => {
                            if (!live) onUpdate({ videoFile, videoUploaded: false });
                            else if (videoFile) {
                              live.startVideoUpload(step, index + 1, videoFile, savedVideo);
                            }
                          }}
                          file={live ? null : step.videoFile}
                          withPreviewList={!live}
                          isVideo
                          maxSize={STEP_VIDEO_MAX_SIZE}
                          onDropRejected={onRejected}
                          className="p-8"
                        >
                          <DropzoneCopy
                            title="Click to Upload or Drag and Drop your File"
                            hints={[
                              'Supported file types',
                              'MP4, AVI, MKV, MOV, WMV, FLV, 3GP, MPEG, up to 30 GB',
                            ]}
                          />
                        </Dropzone>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {step.type === 'scorm' && (
              <div className="flex flex-col gap-3">
                {scormUpload && live ? (
                  <StepScormUploadStatusCard
                    upload={scormUpload}
                    onCancel={() => live.cancelScormUpload(step)}
                    onRetry={() => live.retryScormUpload(step)}
                  />
                ) : (
                  <>
                    {savedScorm?.processingStatus === 'uploading' && (
                      <div className="flex flex-col gap-2">
                        <StepScormServerStatus scorm={savedScorm} />
                        <RemoveButton
                          label="Remove SCORM package"
                          onPress={() => onRequestRemoveMedia('scorm')}
                        />
                      </div>
                    )}
                    {savedScorm && savedScorm.processingStatus !== 'uploading' && scormBadge && (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm text-grey-secondary">
                            SCORM package: {savedScorm.title}
                            {savedScorm.version ? ` (${savedScorm.version})` : ''}
                          </p>
                          <span
                            className="inline-flex h-6 items-center rounded-[6px] px-2 text-xs font-semibold"
                            style={{ backgroundColor: scormBadge.bg, color: scormBadge.text }}
                          >
                            {scormBadge.label}
                          </span>
                        </div>
                        {savedScorm.processingStatus === 'ready' &&
                          savedScorm.isValid &&
                          !step.scormFile &&
                          onPreviewScorm && (
                          <Button
                            className="w-fit bg-transparent text-gray-500 text-[14px] p-0"
                            isLoading={isScormPreviewLoading}
                            disabled={isScormPreviewLoading}
                            onPress={() => onPreviewScorm(savedScorm.packageId)}
                          >
                            Preview SCORM <Svg Icon={OpenedEyeIcon} width={20} height={20} />
                          </Button>
                        )}
                        {savedScorm.processingStatus === 'failed' && (
                          <p className="text-red-tertiary text-sm">
                            {savedScorm.processingError ||
                              savedScorm.validationErrors?.[0] ||
                              'SCORM package is invalid'}
                          </p>
                        )}
                        {!step.scormFile && (
                          <RemoveButton
                            label="Remove SCORM package"
                            // The backend refuses (409) while the package is being processed
                            isDisabled={isSavedScormProcessing}
                            onPress={() => onRequestRemoveMedia('scorm')}
                          />
                        )}
                      </div>
                    )}
                    <FieldLabel>{getScormDropzoneLabel(savedScorm)}</FieldLabel>
                    <Dropzone
                      setFile={selectScormFile}
                      file={live ? null : step.scormFile}
                      withPreviewList={!live}
                      isScorm
                      maxSize={SCORM_MAX_SIZE}
                      onDropRejected={onRejected}
                      // The backend refuses (409) to replace a package that is being processed
                      disabled={isSavedScormProcessing}
                      className="p-8"
                    >
                      <DropzoneCopy
                        title="Drag and drop your SCORM .zip file here"
                        hints={[
                          'Supported formats: SCORM 1.2 and SCORM 2004, up to 5 GB',
                          'The .zip file must include imsmanifest.xml',
                        ]}
                      />
                    </Dropzone>
                    {isSavedScormProcessing && (
                      <p className="text-xs text-grey-secondary">
                        The package can be replaced when processing is finished
                      </p>
                    )}
                    {!live && savedScorm && step.scormFile && (
                      <p className="text-sm text-red">
                        Attention! If you upload a new package, the old package and students&apos;
                        SCORM progress will be deleted.
                      </p>
                    )}
                  </>
                )}
                <Checkbox
                  isSelected={step.scormGraded}
                  isDisabled={isScormSettingsLocked}
                  onValueChange={(graded) => changeScormSettings(graded, step.scormPassingScore)}
                  classNames={{ label: 'text-sm !text-black-headline' }}
                >
                  Graded
                </Checkbox>
                <div
                  className={cn('flex flex-col gap-2', {
                    'opacity-50': !step.scormGraded || isScormSettingsLocked,
                  })}
                >
                  <span className="text-base text-black-headline">SCORM settings</span>
                  <div className="flex items-center gap-2 text-sm text-grey-description">
                    <span className="shrink-0">Passed when score ≥</span>
                    <CustomSelect
                      style="black"
                      aria-label="Passing score percent"
                      className="w-28"
                      items={getPassingScoreOptions(step.scormPassingScore)}
                      isDisabled={!step.scormGraded || isScormSettingsLocked}
                      disallowEmptySelection
                      selectedKeys={new Set([String(step.scormPassingScore)])}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys as Set<string>)[0];
                        if (value && Number(value) !== step.scormPassingScore) {
                          changeScormSettings(step.scormGraded, Number(value));
                        }
                      }}
                    />
                  </div>
                </div>
                {isScormSettingsLocked && (
                  <p className="text-xs text-grey-secondary">
                    The score settings can be changed once the upload is finished
                  </p>
                )}
              </div>
            )}
            {step.type && (
              <div className="flex flex-col gap-2">
                {step.type !== 'general' && <FieldLabel>Upload File</FieldLabel>}
                <Dropzone
                  files={step.attachments}
                  setFiles={changeAttachments}
                  dataBaseFiles={dataBaseFiles}
                  handleRemoveDataBaseFile={removeSavedAttachment}
                  multiple
                  withPreviewList
                  accept={SESSION_ATTACHMENT_ACCEPT}
                  maxSize={MAX_FILE_SIZE}
                  onDropRejected={onRejected}
                  className="p-8"
                >
                  <DropzoneCopy
                    title="Upload or Drag and Drop your File"
                    hints={[
                      MAX_FILE_SIZE_HINT,
                      SESSION_ATTACHMENT_TYPES_HINT,
                    ]}
                  />
                </Dropzone>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </Reorder.Item>
  );
};
